<template>
  <div>
    <sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="refetchData"
      :center-options="centerOptions"
      :sector-options="sectorOptions"
      :type-options="cameraTypeOptions"
      :unit-options="unitOptions"
    />
    <toastification-loading-content ref="loadingToast" />

    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <cameras-wizard
      v-if="isWizardActive"
      :center-options="centerOptions"
      :dvr-options="dvrOptions"
      :reseller-options="resellerOptions"
      :sector-options="sectorOptions"
      :type-options="cameraTypeOptions"
      :unit-options="unitOptions"
      @close-wizard="isWizardActive = false"
      @refresh="refetchData"
    />

    <cameras-form-wizard
      v-if="isWizardFormActive"
      :item-edit="itemEdit"
      :type-options="cameraTypeOptions"
      :center-options="centerOptions"
      :dvr-options="dvrOptions"
      :reseller-options="resellerOptions"
      :sector-options="sectorOptions"
      :unit-options="unitOptions"
      @close-wizard="isWizardFormActive = false"
      @refresh="refetchData"
    />

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :dvr-filter.sync="dvrFilter"
          :dvr-options="dvrOptions"
          @update-camera-data="updateCameras"
          :button-update="buttonUpdate"
          :center-filter.sync="centerFilter"
          :center-options="centerOptions"
          :sector-filter.sync="sectorFilter"
          :sector-options="sectorOptions"
          :tenant-filter.sync="tenantFilter"
          :tenant-options="tenantOptions"
          :unit-filter.sync="unitFilter"
          :unit-options="unitOptions"
          :device-tag-filter.sync="deviceTagFilter"
          :device-tag-options="deviceTagOptions"
          :online-filter.sync="onlineFilter"
          :rtsp-available-filter.sync="rtspAvailableFilter"
          :is-fisheye-filter.sync="isFisheyeFilter"
          :send-to-seproban-filter.sync="sendToSeprobanFilter"
        />
      </template>
      <template v-slot:footer-buttons>
        <b-button
          variant="secondary"
          class="float-right text-capitalize"
          size="sm"
          :disabled="buttonUpdate"
          @click="updateCameras()"
        >
          {{ $t("actions.update") + " " + $tc("hanwha_camera.title", 1) }}
        </b-button>
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card class="mx-2 mt-1" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search')"
              />
              <bulk-edit-button
                v-if="$can('update', 'HanwhaCamera')"
                :is-bulk-edit-mode="isBulkEditMode"
                :selected-count="bulkSelectedCount"
                :has-selection="bulkHasSelection"
                :select-all-matching="bulkSelectAllMatching"
                @enable-multiselect="startBulkMultiSelect"
                @enable-select-all="startBulkSelectAll"
                @open-bulk-modal="openBulkModal"
                @disable-mode="disableBulkEditMode"
              />
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="computedTableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
        no-sort-reset
      >
        <!-- Column: bulkSelect (header) -->
        <template #head(bulkSelect)>
          <b-form-checkbox
            :checked="allVisibleSelected || bulkSelectAllMatching"
            @change="toggleSelectAllVisible"
            :indeterminate="someVisibleSelected && !allVisibleSelected && !bulkSelectAllMatching"
          />
        </template>

        <template #cell(send_to_seproban)="data">
            <div class="text-center">
              <feather-icon :icon="data.item.device.send_to_seproban ? 'CheckCircleIcon' : 'XCircleIcon'"
                :class="data.value ? 'text-success' : 'text-danger'" size="18" />
            </div>
          </template>

          <template #cell(is_fisheye)="data">
            <div class="text-center">
              <feather-icon :icon="data.item.is_fisheye ? 'CheckCircleIcon' : 'XCircleIcon'"
                :class="data.value ? 'text-success' : 'text-danger'" size="18" />
            </div>
          </template>


          <template #cell(channel)="data">
            <div class="text-center">
              {{ data.item.channel + 1 }}
            </div>
          </template>

        <!-- Column: bulkSelect (cell) -->
        <template #cell(bulkSelect)="data">
          <b-form-checkbox
            :checked="isSelected(data.item.id)"
            @change="toggleRowSelection(data.item.id)"
            @click.native.stop
          />
        </template>

        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #head(dvr)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <!-- Column: User -->
        <template #cell(name)="data">
          <b-media vertical-align="center">
            <template #aside>
              <div
                :style="`background: ${data.item.device.online ? 'green' : 'red'};
                        width: 1.5rem;
                        height: 1.5rem;
                        border-radius: 1rem;
                        text-align: center;`"
              ></div>
            </template>
            <small>{{ data.item.name }}</small>
          </b-media>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            :id="`actions-${data.item.id}`"
            tabindex="0"
            variant="white"
            class="px-1 btn-y"
          >
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </b-button>
          <b-popover
            :target="`actions-${data.item.id}`"
            triggers="focus"
            placement="bottom"
            custom-class="popover-p-0"
            variant="white"
          >
            <b-list-group class="m-0">
              <b-list-group-item
                v-if="$can('update', 'HanwhaCamera')"
                class="border-0"
                button
                @click="openEdit(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.edit") }}
                </span>
              </b-list-group-item>
              <b-list-group-item
                class="border-0"
                button
                @click="openModalInfo(data.item)"
              >
                <feather-icon icon="InfoIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("hanwha_camera.device_information") }}
                </span>
              </b-list-group-item>
              <b-list-group-item
                v-if="$can('delete', 'HanwhaCamera')"
                class="border-0"
                button
                @click="deleteCamera(data.item)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.delete") }}
                </span>
              </b-list-group-item>
              <b-list-group-item
                v-if="$can('delete', 'HanwhaCamera')"
                class="border-0"
                button
                @click="syncProfiles(data.item)"
              >
                <feather-icon icon="RefreshCwIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  Sincronizar configuración
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <DeviceInfo
      :hanwhaCamera="currentDeviceSelected"
      :isModalInfoActive="isModalInfoActive"
      @close-modal="closeModalInfo"
      :is-active.sync="isModalInfoActive"
      v-if="isModalInfoActive"/>

    <!-- Bulk Edit Modal -->
    <bulk-edit-modal
      :is-open="isBulkModalOpen"
      :fields="bulkEditFields"
      :form-data="bulkFormData"
      :selected-count="bulkSelectedCount"
      :is-submitting="isBulkSubmitting"
      :catalog-options="bulkCatalogOptions"
      @close="closeBulkModal"
      @submit="submitBulkEdit"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BInputGroup,
  BFormCheckbox,
} from "bootstrap-vue";
import axios from "@/libs/axios";
import vSelect from "vue-select";
import { ref, computed, watch } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import Filters from "./Filters.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import DeviceInfo from "./DeviceInfo.vue";
import CamerasWizard from './CamerasWizard/index.vue'
import CamerasFormWizard from './CamerasFormWizard/Index.vue';
import BulkEditButton from "@/components/BulkEdit/BulkEditButton.vue";
import BulkEditModal from "@/components/BulkEdit/BulkEditModal.vue";
import useBulkEdit from "@/composables/useBulkEdit";
import { BULK_EDIT_FIELDS, BULK_EDIT_ENDPOINT, BULK_EDIT_IDS_KEY, BULK_EDIT_BATCH_STATUS_ENDPOINT } from "./bulkEditConfig";

export default {
  components: {
    Sidenav,
    BreadcrumbMenu,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    vSelect,
    BFormCheckbox,
    ToastificationLoadingContent,
    FilterModal,
    ButtonFilters,
    DeviceInfo,
    CamerasWizard,
    CamerasFormWizard,
    BulkEditButton,
    BulkEditModal
  },

  setup(_, context) {
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const loadingToast = ref(null);
    const buttonUpdate = ref(false);
    const currentDeviceSelected = ref(null);
    const isModalInfoActive = ref(false);
    const isWizardActive = ref(false);
    const isWizardFormActive = ref(false);

    const openEdit = (item) => {
      isWizardFormActive.value = true;
      itemEdit.value = item;

      itemEdit.value.id_unit = item.units.map((value) => ({
        value: value.id,
        label: value.description,
        id_tenant: value.router.id_tenant,
      }));

      isAdd.value = false;
    };

    const openModalInfo = (camera) => {
      currentDeviceSelected.value = camera
      isModalInfoActive.value = true
    };
    const closeModalInfo = () => {
      currentDeviceSelected.value = null
      isModalInfoActive.value = false
    };
    const { t } = useI18nUtils();

    const toast = useToast();

    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "hanwha_camera.dvr_validation": t("hanwha_camera.dvr_validation"),
    };

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      dvrOptions,
      dvrFilter,
      centerOptions,
      centerFilter,
      sectorOptions,
      sectorFilter,
      tenantOptions,
      tenantFilter,
      resellerOptions,
      resellerFilter,
      cameraTypeOptions,
      unitOptions,
      unitFilter,
      deviceTagOptions,
      deviceTagFilter,
      onlineFilter,
      rtspAvailableFilter,
      isFisheyeFilter,
      sendToSeprobanFilter,
      clearSearchFilters,
      filtersCount,
      buildFilters,
      deleteCamera,
    } = useList();

    // --- Bulk Edit ---
    // Active (non-null) list filters, sent as `filters` and used to scope select-all.
    const buildActiveFilters = () => {
      const candidates = {
        q: searchQuery.value,
        dvr: dvrFilter.value,
        center: centerFilter.value,
        sector: sectorFilter.value,
        tenant: tenantFilter.value,
        units: unitFilter.value,
        device_tag: deviceTagFilter.value,
        online: onlineFilter.value,
        rtsp_available: rtspAvailableFilter.value,
        is_fisheye: isFisheyeFilter.value,
        send_to_seproban: sendToSeprobanFilter.value,
      };
      const active = {};
      Object.entries(candidates).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          active[key] = value;
        }
      });
      return active;
    };

    const bulkEdit = useBulkEdit({
      endpoint: BULK_EDIT_ENDPOINT,
      fields: BULK_EDIT_FIELDS,
      idsKey: BULK_EDIT_IDS_KEY,
      batchStatusEndpoint: BULK_EDIT_BATCH_STATUS_ENDPOINT,
      getSelectAllExtras: buildActiveFilters,
      onSuccess: () => {
        refetchData();
      },
    });

    watch(
      totalList,
      (n) => {
        bulkEdit.totalCount.value = n || 0;
      },
      { immediate: true }
    );

    const booleanOptions = computed(() => [
      { label: t("yes"), value: true },
      { label: t("no"), value: false },
    ]);

    const bulkCatalogOptions = computed(() => ({
      dvrOptions: dvrOptions.value,
      tenantOptions: tenantOptions.value,
      centerOptions: centerOptions.value,
      sectorOptions: sectorOptions.value,
      unitOptions: unitOptions.value,
      deviceTagOptions: deviceTagOptions.value,
      booleanOptions: booleanOptions.value,
    }));

    const computedTableColumns = computed(() => {
      if (bulkEdit.isBulkEditMode.value) {
        return [
          { key: "bulkSelect", label: "", sortable: false, thStyle: "width: 40px" },
          ...tableColumns,
        ];
      }
      return tableColumns;
    });

    const visibleItems = computed(() => refListTable.value?.localItems || []);

    const allVisibleSelected = computed(() => {
      const visible = visibleItems.value;
      if (!visible.length) return false;
      return visible.every((item) => bulkEdit.isSelected(item.id));
    });

    const someVisibleSelected = computed(() => {
      const visible = visibleItems.value;
      if (!visible.length) return false;
      return visible.some((item) => bulkEdit.isSelected(item.id));
    });

    const startBulkMultiSelect = () => {
      bulkEdit.clearSelection();
      bulkEdit.enableBulkEditMode();
    };

    const startBulkSelectAll = () => {
      bulkEdit.enableBulkEditMode();
      bulkEdit.enableSelectAllMatching();
    };

    const toggleSelectAllVisible = () => {
      if (bulkEdit.selectAllMatching.value) {
        bulkEdit.clearSelection();
        return;
      }
      const visible = visibleItems.value;
      if (allVisibleSelected.value) {
        visible.forEach((item) => {
          if (bulkEdit.isSelected(item.id)) bulkEdit.toggleItem(item.id);
        });
      } else {
        visible.forEach((item) => {
          if (!bulkEdit.isSelected(item.id)) bulkEdit.toggleItem(item.id);
        });
      }
    };

    // Row checkbox: unchecking a row while in select-all exits that mode, keeping the rest.
    const toggleRowSelection = (id) => {
      if (bulkEdit.selectAllMatching.value) {
        const remaining = visibleItems.value
          .map((item) => item.id)
          .filter((itemId) => itemId !== id);
        bulkEdit.setSelectedIds(remaining);
        return;
      }
      bulkEdit.toggleItem(id);
    };

    const updateCameras = () => {
      isWizardActive.value = true;

      return

      loadingToast.value.show("Actualizando", `Actualizando cámaras ...`, "secondary");
      buttonUpdate.value = true;

      store
        .dispatch("hanwha_camera/discover", {
          dvrs: dvrFilter.value,
        })
        .then((response) => {
          if (response.success) {
            loadingToast.value.close();
            buttonUpdate.value = false;
            toast({
              component: ToastificationContent,
              props: {
                title: "Actualizado Correctamente",
                icon: "CheckIcon",
                variant: "success",
              },
            });
            refetchData();
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
            buttonUpdate.value = false;
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const syncProfiles = async (item) => {
      await axios.get(`/v1/hanwha/camera/${item.id}/sync-data`)
        .then(response => {
          const { data } = response;
          console.log('data', data.data);
          toast({
            component: ToastificationContent,
            props: {
              title: data.message,
              icon: "CheckIcon",
              variant: "success",
            },
          });
        })
        .catch(error => {
          const { data } = error.response;

          toast({
            component: ToastificationContent,
            props: {
              title: data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
    }

    return {
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      deleteCamera,
      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      dvrOptions,
      dvrFilter,
      loadingToast,
      updateCameras,
      buttonUpdate,
      centerOptions,
      centerFilter,
      sectorOptions,
      sectorFilter,
      tenantOptions,
      tenantFilter,
      resellerOptions,
      resellerFilter,
      cameraTypeOptions,
      unitOptions,
      unitFilter,
      deviceTagOptions,
      deviceTagFilter,
      onlineFilter,
      rtspAvailableFilter,
      isFisheyeFilter,
      sendToSeprobanFilter,
      currentDeviceSelected,
      clearSearchFilters,
      filtersCount,
      isModalInfoActive,
      buildFilters,
      openModalInfo,
      closeModalInfo,
      isWizardActive,
      isWizardFormActive,

      // Bulk Edit
      isBulkEditMode: bulkEdit.isBulkEditMode,
      bulkSelectedCount: bulkEdit.selectedCount,
      bulkHasSelection: bulkEdit.hasSelection,
      bulkSelectAllMatching: bulkEdit.selectAllMatching,
      toggleBulkEditMode: bulkEdit.toggleBulkEditMode,
      disableBulkEditMode: bulkEdit.disableBulkEditMode,
      startBulkMultiSelect,
      startBulkSelectAll,
      openBulkModal: bulkEdit.openBulkModal,
      closeBulkModal: bulkEdit.closeBulkModal,
      submitBulkEdit: bulkEdit.submitBulkEdit,
      isBulkModalOpen: bulkEdit.isBulkModalOpen,
      bulkEditFields: BULK_EDIT_FIELDS,
      bulkFormData: bulkEdit.formData,
      isBulkSubmitting: bulkEdit.isSubmitting,
      bulkCatalogOptions,
      computedTableColumns,
      toggleItem: bulkEdit.toggleItem,
      toggleRowSelection,
      isSelected: bulkEdit.isSelected,
      allVisibleSelected,
      someVisibleSelected,
      toggleSelectAllVisible,
      syncProfiles
    };
  },
};
</script>
